import {
  Filter,
  Snowflake,
  Fan,
  Droplets,
  Box,
  AlignJustify,
  Wind,
  Sparkles,
  Leaf,
  Gauge,
  ShieldCheck,
  Check,
  Phone,
  MessageCircle,
  CalendarClock,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { CTAButton } from "@/components/CTAButton";
import { Section, Breadcrumb, CTABanner } from "@/components/Sections";
import { VideoPlayer } from "@/components/VideoPlayer";
import { BUSINESS } from "@/lib/site";

const CLEANED = [
  { Icon: Filter, title: "Filters", text: "Thoroughly washed to remove accumulated dust and dirt and restore clean, unrestricted airflow." },
  { Icon: Snowflake, title: "Evaporator Coil", text: "Deep cleaned to remove embedded dust, dirt and buildup from the coil." },
  { Icon: Fan, title: "Blower Fan / Fan Roller", text: "Thoroughly cleaned to remove heavy dirt and buildup that can restrict airflow and reduce performance." },
  { Icon: Droplets, title: "Drain Tray & Drainage System", text: "Cleaned thoroughly to remove dirt, residue and buildup and help maintain proper condensate drainage." },
  { Icon: Box, title: "Internal AC Unit", text: "The indoor AC unit is thoroughly deep cleaned internally, including the casing and internal areas where dirt and contamination accumulate." },
  { Icon: AlignJustify, title: "Air Outlet & Louvers", text: "Cleaned thoroughly to remove accumulated dust and dirt from the air outlet and directional louvers." },
];

const PROCESS = [
  { n: "01", title: "Protect the Work Area", text: "We prepare and protect the surrounding area before the cleaning process begins." },
  { n: "02", title: "Open & Prepare the Indoor Unit", text: "The unit is opened and prepared so the internal components can be properly cleaned." },
  { n: "03", title: "Apply Cleaning Treatment", text: "Professional cleaning solution is applied to loosen accumulated dirt and contamination." },
  { n: "04", title: "Deep Clean the Internal Unit", text: "The evaporator coil, blower fan, filters, drainage system, airflow outlets and internal components are thoroughly cleaned." },
  { n: "05", title: "Mould Removal & Antibacterial Treatment", text: "Mould and contamination buildup are removed and antibacterial treatment is applied as part of the cleaning process." },
  { n: "06", title: "Reassemble & Test", text: "The AC is carefully reassembled and tested for proper operation and airflow before the job is completed." },
];

const BENEFITS = [
  { Icon: Wind, title: "Better Airflow", text: "Removing accumulated dirt from the coil and blower helps air move more freely through the system." },
  { Icon: Sparkles, title: "Cleaner Internal Unit", text: "Dust, dirt, mould buildup and contamination are removed from inside the AC rather than only cleaning the filters." },
  { Icon: Leaf, title: "Removes Unpleasant AC Smells", text: "A proper deep clean removes the dirt, mould and bacterial buildup responsible for the typical unpleasant or musty smell from an air conditioner." },
  { Icon: Gauge, title: "Better Operating Performance", text: "A clean coil, blower and airflow path allow the system to operate more effectively." },
  { Icon: Droplets, title: "Cleaner Drainage System", text: "Cleaning the drain tray and drainage system helps remove accumulated dirt and reduce drainage-related problems." },
];

const SIGNS = [
  "Unpleasant or musty smell when the AC starts",
  "Weak or restricted airflow",
  "Visible dirt inside the air outlet",
  "Dirt visible on the blower fan",
  "Dust buildup around the louvers",
  "Not professionally deep cleaned for more than a year",
  "Heavy seasonal use",
  "Water or dirt buildup around the drainage area",
];

// Two-column info row with a small icon, title and description — divider-separated, no cards.
function InfoRow({ Icon, title, children }) {
  return (
    <Reveal className="grid grid-cols-1 gap-3 border-t border-border py-6 sm:grid-cols-[minmax(0,230px)_1fr] sm:gap-10">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 shrink-0 text-brand" strokeWidth={1.75} />
        <h3 className="font-heading text-lg font-medium tracking-tight text-charcoal">{title}</h3>
      </div>
      <p className="text-base leading-relaxed text-charcoal-muted">{children}</p>
    </Reveal>
  );
}

function ProcessRow({ n, title, children }) {
  return (
    <Reveal className="grid grid-cols-1 gap-3 border-t border-border py-7 sm:grid-cols-[minmax(0,90px)_1fr] sm:gap-10">
      <span className="font-heading text-3xl font-medium tabular-nums text-brand sm:text-4xl">{n}</span>
      <div>
        <h3 className="font-heading text-xl font-medium tracking-tight text-charcoal">{title}</h3>
        <p className="mt-2 text-base leading-relaxed text-charcoal-muted">{children}</p>
      </div>
    </Reveal>
  );
}

export default function DeepCleaning() {
  return (
    <>
      <SEO
        title="AC Deep Cleaning Paphos | NorthWork Cyprus"
        description="Professional AC deep cleaning in Paphos — internal cleaning, mould removal and antibacterial treatment to remove dirt, buildup and bad smells. Air conditioning cleaning service by NorthWork Cyprus."
        path="/ac-deep-cleaning-paphos"
        schema={{ "@type": "Service", serviceType: "AC Deep Cleaning", areaServed: "Paphos" }}
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "AC Deep Cleaning" }]} />

      {/* Hero */}
      <section data-testid="page-hero" className="relative isolate overflow-hidden border-b border-border">
        <img
          src="/images/deep-cleaning-hero-bg.webp"
          alt="NorthWork Cyprus technician deep cleaning an indoor air-conditioning unit with protective wash-bag equipment in Paphos"
          loading="eager"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[70%_38%] md:object-right"
        />
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0e14]/95 via-[#0b0e14]/70 to-[#0b0e14]/25 md:via-[#0b0e14]/80 md:to-[#0b0e14]/40"
          aria-hidden="true"
        />
        <div className="container-x py-10 md:py-14 lg:py-24">
          <div className="max-w-2xl">
            <Reveal variant="up">
              <h1 className="font-heading text-[2.5rem] font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[3.6rem]">
                Professional AC Deep Cleaning in Paphos
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-5 space-y-3">
              <p className="text-base leading-relaxed text-white/85 lg:text-lg">
                More than a standard filter clean. NorthWork Cyprus provides thorough indoor AC deep
                cleaning designed to remove accumulated dust, dirt, mould buildup and contamination
                from inside the unit.
              </p>
              <p className="hidden text-sm leading-relaxed text-white/70 sm:block lg:text-base">
                Our service includes detailed internal cleaning, antibacterial treatment, drainage
                cleaning and a complete operational check before the unit is reassembled.
              </p>
            </Reveal>
            <Reveal delay={0.15} variant="up" className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} icon={Phone} testid="hero-call-cta">
                Call {BUSINESS.phoneDisplay}
              </CTAButton>
              <CTAButton
                href={BUSINESS.whatsappUrl}
                variant="secondary"
                icon={MessageCircle}
                testid="hero-whatsapp-cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </CTAButton>
              <CTAButton to="/contact" variant="secondary" testid="hero-primary-cta">Book AC Deep Cleaning</CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Video */}
      <Section className="border-t border-border bg-surface py-16 lg:py-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <Reveal>
              <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                See Our AC Deep Cleaning Process
              </h2>
            </Reveal>
            <Reveal delay={0.05} className="mt-4">
              <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
                See how NorthWork Cyprus carries out a complete professional AC deep clean — from
                preparation and dismantling through washing, treatment, reassembly and testing.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="mx-auto mt-10 max-w-5xl lg:mt-12">
            <VideoPlayer
              src="/media/deep-clean-process.mp4"
              poster="/media/deep-clean-process-poster.webp"
              label="NorthWork Cyprus professional AC deep cleaning"
              testid="deep-clean-video"
            />
          </Reveal>
        </div>
      </Section>

      {/* What gets cleaned */}
      <Section className="border-t border-border bg-surface py-16 lg:py-24">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              What Gets Cleaned
            </h2>
          </Reveal>
          <div className="mt-10 border-b border-border">
            {CLEANED.map((it) => (
              <InfoRow key={it.title} Icon={it.Icon} title={it.title}>{it.text}</InfoRow>
            ))}
          </div>
        </div>
      </Section>

      {/* Mould removal & antibacterial treatment */}
      <Section className="py-16 lg:py-24">
        <div className="container-x">
          <Reveal>
            <div className="border-l-2 border-brand pl-6 sm:pl-8 lg:max-w-4xl">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.75} />
                <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
                  Mould Removal &amp; Antibacterial Treatment
                </h2>
              </div>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-charcoal-muted lg:text-lg">
                <p>
                  Dust and moisture inside an air conditioner can lead to mould, bacterial buildup
                  and unpleasant odours over time.
                </p>
                <p>
                  During our deep-cleaning process, accumulated dirt, mould buildup and contamination
                  are thoroughly removed from the indoor unit.
                </p>
                <p>
                  After cleaning, antibacterial treatment is applied as part of the service before
                  the unit is reassembled and tested.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Process */}
      <Section className="py-16 lg:py-24">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              How We Deep Clean Your AC
            </h2>
          </Reveal>
          <div className="mt-10 border-b border-border lg:mt-12">
            {PROCESS.map((s) => (
              <ProcessRow key={s.n} n={s.n} title={s.title}>{s.text}</ProcessRow>
            ))}
          </div>
        </div>
      </Section>

      {/* Why it matters */}
      <Section className="border-t border-border bg-surface py-16 lg:py-24">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Why Professional AC Deep Cleaning Matters
            </h2>
          </Reveal>
          <div className="mt-10 border-b border-border">
            {BENEFITS.map((it) => (
              <InfoRow key={it.title} Icon={it.Icon} title={it.title}>{it.text}</InfoRow>
            ))}
          </div>
        </div>
      </Section>

      {/* How often */}
      <Section className="py-16 lg:py-24">
        <div className="container-x grid gap-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                How Often Should Your AC Be Deep Cleaned?
              </h2>
            </Reveal>
            <Reveal delay={0.05} className="mt-6 flex items-center gap-3">
              <CalendarClock className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.75} />
              <span className="font-heading text-xl font-medium text-charcoal sm:text-2xl">
                At least once a year
              </span>
            </Reveal>
          </div>
          <Reveal delay={0.05} className="space-y-5 text-base leading-relaxed text-charcoal-muted lg:col-span-7 lg:text-lg">
            <p>We recommend having your air conditioner professionally deep cleaned at least once a year.</p>
            <p>
              In Cyprus, air-conditioning systems often operate heavily for long periods during the
              summer, allowing dust, moisture and dirt to accumulate inside the unit.
            </p>
            <p>
              Air conditioners used very frequently, in dusty environments, rental properties,
              commercial spaces or homes with pets may benefit from more frequent cleaning.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Signs */}
      <Section className="border-t border-border bg-surface py-16 lg:py-24">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Signs Your AC May Need Deep Cleaning
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-x-12 gap-y-4 sm:grid-cols-2">
            {SIGNS.map((s) => (
              <Reveal key={s} className="flex items-start gap-3 border-t border-border pt-4">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={2} />
                <span className="text-base leading-relaxed text-charcoal-muted">{s}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <CTABanner
        title="Ready for a Proper AC Deep Clean?"
        text="Professional internal AC deep cleaning for built-up dust, dirt and debris inside the unit — available across Paphos and surrounding areas."
        primaryLabel="Book Deep Cleaning"
        primaryTo="/contact"
        image="/images/deep-clean-cta.webp"
        paddingClass="py-16 lg:py-24"
        overlayStyle="linear-gradient(to right, rgba(6,16,32,0.80) 0%, rgba(6,16,32,0.66) 35%, rgba(6,16,32,0.56) 60%, rgba(6,16,32,0.40) 100%)"
        showWhatsapp
      />
    </>
  );
}
